import React, { useMemo } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Typography } from '@mui/material';

import { VirtualCreditCardState, VirtualCreditCardStateComponent } from './VirtualCreditCardState';

type Props = {
  cardNumber?: string;
  expiry?: string;
  cvv?: string;
  nameOnCard?: string;
  balance?: number;
  currencyCode?: string;
  showBalance?: boolean;
  state?: (typeof VirtualCreditCardState)[keyof typeof VirtualCreditCardState];
};

export function VirtualCreditCardComponent({
  cardNumber = 'XXXX XXXX XXXX XXXX',
  expiry = 'XX/XX',
  cvv = 'XXX',
  nameOnCard = 'Luxury Escapes',
  balance = 0,
  currencyCode = 'XXX',
  showBalance = true,
  state,
}: Props) {
  // Returns the formatted expiry date in MM/YY format
  const formattedExpiryDate = useMemo(() => {
    if (!expiry || expiry.trim().length !== 4) {
      return 'XX/XX';
    }

    const year = expiry.slice(0, 2);
    const month = expiry.slice(2, 4);

    return `${month}/${year}`;
  }, [expiry]);

  return (
    <Box
      data-testid="vcc-card"
      sx={{
        width: { xs: '100%', sm: 360 },
        height: 225,
        borderRadius: 3,
        background: 'linear-gradient(45deg, #000000 30%, #424242 90%)',
        p: 3,
        color: 'white',
        position: 'relative',
        boxShadow: 3,
      }}
    >
      <Box sx={{ display: 'flex', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VirtualCreditCardStateComponent state={state} />
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 1,
              px: 1.5,
              py: 0.75,
              backdropFilter: 'blur(4px)',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                color: 'white',
                fontWeight: 500,
                letterSpacing: 0.5,
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            >
              {showBalance ? `Balance: ${balance} ${currencyCode}` : 'XXX'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography
          variant="h6"
          sx={{
            letterSpacing: 2,
            color: 'white',
            fontFamily: 'monospace',
            fontWeight: 500,
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {/* Adding a space after every 4 characters and removing any trailing spaces */}
          {cardNumber?.replace(/([A-Za-z0-9]{4})/g, '$1 ').trim()}
        </Typography>
        <IconButton
          size="small"
          onClick={() => navigator.clipboard.writeText(cardNumber?.replace(/\s/g, ''))}
          sx={{ color: 'white', opacity: 0.7, '&:hover': { opacity: 1 } }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.7, color: 'white' }}>
            Expiry Date (MM/YY)
          </Typography>
          <Typography sx={{ color: 'white' }}>{formattedExpiryDate}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ opacity: 0.7, color: 'white' }}>
            CVV
          </Typography>
          <Typography sx={{ color: 'white' }}>{cvv}</Typography>
        </Box>
      </Box>
      <Box>
        <Typography variant="caption" sx={{ opacity: 0.7, color: 'white', mb: 0 }}>
          Name on Card
        </Typography>
        <Typography sx={{ mb: 1, color: 'white', lineHeight: 1 }}>{nameOnCard}</Typography>
      </Box>
    </Box>
  );
}
